import React from 'react';
import { Globe } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import { SupportedLanguage } from '../../i18n/translations';

interface LanguageSwitcherProps {
  variant?: 'compact' | 'pills' | 'dark';
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'compact',
  className = '',
}) => {
  const { language, setLanguage, supportedLanguages, t } = useLanguage();

  if (variant === 'pills') {
    return (
      <div
        className={`inline-flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200 ${className}`}
        role="group"
        aria-label={t('common.selectLanguage', 'Select Language')}
      >
        <Globe className="w-3.5 h-3.5 text-slate-500 ml-1.5 shrink-0" />
        {supportedLanguages.map((lang) => {
          const active = language === lang.code;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => setLanguage(lang.code)}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                active
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'
              }`}
            >
              {lang.nativeLabel}
            </button>
          );
        })}
      </div>
    );
  }

  const isDark = variant === 'dark';

  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`}>
      <Globe className={`w-3.5 h-3.5 shrink-0 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
      <select
        aria-label={t('common.selectLanguage', 'Select Language')}
        value={language}
        onChange={(e) => setLanguage(e.target.value as SupportedLanguage)}
        className={`text-xs font-bold rounded-xl px-2.5 py-1.5 border transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 ${
          isDark
            ? 'bg-slate-900 text-slate-100 border-slate-700 hover:border-purple-500'
            : 'bg-white text-slate-700 border-slate-200 hover:border-purple-300 shadow-2xs'
        }`}
      >
        {supportedLanguages.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.nativeLabel} ({lang.shortLabel})
          </option>
        ))}
      </select>
    </div>
  );
};
